import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, isRef as _isRef, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "home-view" }
const _hoisted_2 = { class: "home-view__content" }
const _hoisted_3 = {
  key: 0,
  class: "home-view__loading"
}
const _hoisted_4 = {
  key: 1,
  class: "home-view__error"
}
const _hoisted_5 = {
  key: 2,
  class: "home-view__empty"
}
const _hoisted_6 = {
  key: 3,
  class: "home-view__groups"
}
const _hoisted_7 = { class: "d-flex align-center" }
const _hoisted_8 = { class: "font-weight-bold" }
const _hoisted_9 = { class: "text-caption" }

import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import PatientListHeader from "../components/PatientListHeader.vue";
import PatientFilterBar from "../components/PatientFilterBar.vue";
import PatientTableHeader from "../components/PatientTableHeader.vue";
import PatientGroupSection from "../components/PatientGroupSection.vue";
import CallDialog from "../components/CallDialog.vue";
import SettingsPanel from "../components/SettingsPanel.vue";
import { usePatientList } from "../composables/usePatientList";
import { usePatientActions } from "../composables/usePatientActions";
import { usePatientStore } from "../store/patientStore";
import { clearAuthToken } from "../utils/auth";
import { signalingService } from "../services/signalingService";
import type { PatientAvailabilityEvent } from "@virtual-care/shared";


export default /*@__PURE__*/_defineComponent({
  __name: 'HomeView',
  setup(__props) {

const {
  patientGroups,
  totalPatientCount,
  filterCounts,
  selectedFilters,
  searchQuery,
  sortColumn,
  sortDirection,
  loading,
  error,
  toggleFilter,
  setSearchQuery,
  setSortColumn,
  toggleGroupExpanded,
  loadPatients,
} = usePatientList();

const {
  callDialogOpen,
  selectedPatientId,
  shouldShowBreakthrough,
  initiateCall,
  handleBreakthrough: initiateBreakthrough,
  confirmCall,
} = usePatientActions();

const patientStore = usePatientStore();
const router = useRouter();

const hospitalName = ref("Community Hospital");
const userName = ref(localStorage.getItem('vc_display_name') || 'Virtual Nurse');
const settingsPanelOpen = ref(false);

// Availability notification state
const showAvailabilityNotification = ref(false);
const availabilityNotificationTitle = ref("");
const availabilityNotificationMessage = ref("");

const units = computed(() => patientStore.units);
const selectedUnit = computed(() => patientStore.selectedUnit);

const selectedPatient = computed(() => {
  if (!selectedPatientId.value) return null;
  for (const group of patientGroups.value) {
    const patient = group.patients.find((p) => p.patientId === selectedPatientId.value);
    if (patient) return patient;
  }
  return null;
});

const handleUnitChange = (unitId: string) => {
  patientStore.setSelectedUnit(unitId);
};

const handleSettings = () => {
  settingsPanelOpen.value = true;
};

const closeSettings = () => {
  settingsPanelOpen.value = false;
};

const handleLogout = () => {
  clearAuthToken();
  router.push('/login');
};

const handleCall = (patientId: string) => {
  const patient = patientGroups.value.flatMap((g) => g.patients).find((p) => p.patientId === patientId);
  if (patient) initiateCall(patient);
};

const handleBreakthrough = (patientId: string) => {
  const patient = patientGroups.value.flatMap((g) => g.patients).find((p) => p.patientId === patientId);
  if (patient) initiateBreakthrough(patient);
};

const handleSearch = (query: string) => {
  setSearchQuery(query);
};

const handleRowExpand = (patientId: string) => {
  console.log("Row expanded:", patientId);
};

// Handle patient availability response
const handlePatientAvailability = (event: PatientAvailabilityEvent) => {
  console.log('[HomeView] Patient availability response:', event);
  
  // Find the patient by roomId
  const patient = patientGroups.value
    .flatMap((g) => g.patients)
    .find((p) => p.room.roomId === event.roomId);
  
  if (!patient) {
    console.warn('[HomeView] Patient not found for roomId:', event.roomId);
    return;
  }
  
  // Update patient store with availability status
  patientStore.setPatientAvailability(patient.patientId, event.response);
  
  // Show notification
  if (event.response === 'available') {
    availabilityNotificationTitle.value = `Patient Available - Room ${patient.room.roomNumber}`;
    availabilityNotificationMessage.value = `${patient.patientName} is now available for a call`;
  } else {
    availabilityNotificationTitle.value = `Patient Response - Room ${patient.room.roomNumber}`;
    availabilityNotificationMessage.value = `${patient.patientName} will call back later`;
  }
  
  showAvailabilityNotification.value = true;
};

onMounted(async () => {
  await patientStore.loadUnits();
  if (patientStore.selectedUnit) {
    loadPatients(patientStore.selectedUnit);
  }
  
  // Listen for patient availability responses
  signalingService.on('patient_availability', handlePatientAvailability);
  console.log('[HomeView] Listening for patient_availability events');
});

onBeforeUnmount(() => {
  // Clean up listener
  signalingService.off('patient_availability', handlePatientAvailability);
  console.log('[HomeView] Removed patient_availability listener');
});

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(PatientListHeader, {
      "hospital-name": hospitalName.value,
      "user-name": userName.value,
      onSettings: handleSettings,
      onLogout: handleLogout
    }, null, 8, ["hospital-name", "user-name"]),
    _createVNode(PatientFilterBar, {
      "total-count": _unref(totalPatientCount),
      "filter-counts": _unref(filterCounts),
      "selected-filters": _unref(selectedFilters),
      "selected-unit": selectedUnit.value,
      units: units.value,
      onToggleFilter: _unref(toggleFilter),
      onUnitChange: handleUnitChange
    }, null, 8, ["total-count", "filter-counts", "selected-filters", "selected-unit", "units", "onToggleFilter"]),
    _createVNode(PatientTableHeader, {
      "sort-column": _unref(sortColumn),
      "sort-direction": _unref(sortDirection),
      "search-query": _unref(searchQuery),
      onSort: _unref(setSortColumn),
      onSearch: handleSearch
    }, null, 8, ["sort-column", "sort-direction", "search-query", "onSort"]),
    _createElementVNode("div", _hoisted_2, [
      (_unref(loading))
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_component_v_progress_circular, {
              indeterminate: "",
              color: "#6B46C1"
            }),
            _cache[4] || (_cache[4] = _createElementVNode("span", null, "Loading patients...", -1))
          ]))
        : (_unref(error))
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(_component_v_icon, {
                size: "48",
                color: "#D73D17"
              }, {
                default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                  _createTextVNode("mdi-alert-circle", -1)
                ]))]),
                _: 1
              }),
              _cache[7] || (_cache[7] = _createElementVNode("span", null, "Failed to load patients. Please try again.", -1)),
              _createVNode(_component_v_btn, {
                color: "#6B46C1",
                onClick: _unref(loadPatients)
              }, {
                default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                  _createTextVNode("Retry", -1)
                ]))]),
                _: 1
              }, 8, ["onClick"])
            ]))
          : (_unref(patientGroups).length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(_component_v_icon, {
                  size: "48",
                  color: "rgba(0, 0, 0, 0.38)"
                }, {
                  default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                    _createTextVNode("mdi-account-search", -1)
                  ]))]),
                  _: 1
                }),
                _cache[9] || (_cache[9] = _createElementVNode("span", null, "No patients found matching your filters.", -1))
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(patientGroups), (group) => {
                  return (_openBlock(), _createBlock(PatientGroupSection, {
                    key: group.id,
                    group: group,
                    onToggle: _unref(toggleGroupExpanded),
                    onCall: handleCall,
                    onBreakthrough: handleBreakthrough,
                    onExpandRow: handleRowExpand
                  }, null, 8, ["group", "onToggle"]))
                }), 128))
              ]))
    ]),
    _createVNode(CallDialog, {
      modelValue: _unref(callDialogOpen),
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(callDialogOpen) ? (callDialogOpen).value = $event : null)),
      "patient-info": selectedPatient.value,
      "show-breakthrough": _unref(shouldShowBreakthrough),
      onConfirm: _unref(confirmCall)
    }, null, 8, ["modelValue", "patient-info", "show-breakthrough", "onConfirm"]),
    _createVNode(SettingsPanel, {
      modelValue: settingsPanelOpen.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((settingsPanelOpen).value = $event)),
      onClose: closeSettings
    }, null, 8, ["modelValue"]),
    _createVNode(_component_v_snackbar, {
      modelValue: showAvailabilityNotification.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showAvailabilityNotification).value = $event)),
      color: "success",
      timeout: "6000",
      location: "top right",
      "close-on-content-click": false
    }, {
      actions: _withCtx(() => [
        _createVNode(_component_v_btn, {
          variant: "text",
          size: "small",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (showAvailabilityNotification.value = false))
        }, {
          default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
            _createTextVNode(" Dismiss ", -1)
          ]))]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_7, [
          _createVNode(_component_v_icon, {
            start: "",
            color: "white"
          }, {
            default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
              _createTextVNode("mdi-check-circle", -1)
            ]))]),
            _: 1
          }),
          _createElementVNode("div", null, [
            _createElementVNode("div", _hoisted_8, _toDisplayString(availabilityNotificationTitle.value), 1),
            _createElementVNode("div", _hoisted_9, _toDisplayString(availabilityNotificationMessage.value), 1)
          ])
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})